import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { array, boolean, Decoder, field, number, oneOf, string, succeed } from 'jsonous';
import { Maybe, nothing } from 'maybeasy';
import { resourceDecoder } from '../../../../../Resource/Decoders';
import { Resource } from '../../../../../Resource/Types';
import {
  LearningCollectionPayloadWithScheduledPrograms,
  LearningCollectionResourceWithScheduledPrograms,
  ProductCollectionKind,
  ProductCollectionResource,
  ScheduledProgram,
} from '../../../LearningCollection/CurrentCollectionStep/BuilderV2/Types';

type ProductCollectionPayload = ProductCollectionResource['payload'];
type ProductCollectionDisplayType = ProductCollectionPayload['display_type'];
type LearningDesignState = ProductCollectionPayload['learning_design_state'];

const productCollectionKindDecoder: Decoder<ProductCollectionKind> = oneOf([
  stringLiteral<ProductCollectionKind>('learning-design'),
  stringLiteral<ProductCollectionKind>('recommendation-engine'),
  stringLiteral<ProductCollectionKind>('dynamic-recommendation'),
  stringLiteral<ProductCollectionKind>('group-coaching'),
  stringLiteral<ProductCollectionKind>('ai-recommendation'),
]);

const displayTypeDecoder: Decoder<ProductCollectionDisplayType> = oneOf([
  stringLiteral<ProductCollectionDisplayType>('carousel'),
  stringLiteral<ProductCollectionDisplayType>('list'),
  stringLiteral<ProductCollectionDisplayType>('grid'),
]);

const learningDesignStateDecoder: Decoder<LearningDesignState> = oneOf([
  stringLiteral<LearningDesignState>('custom'),
  stringLiteral<LearningDesignState>('curated'),
  stringLiteral<LearningDesignState>('browse-all'),
  stringLiteral<LearningDesignState>('build-your-own'),
  stringLiteral<LearningDesignState>('blank'),
]);

const productCollectionPayloadDecoder: Decoder<ProductCollectionPayload> = succeed({})
  .assign('id', field('id', explicitMaybe(number)))
  .assign('name', field('name', string))
  .assign('description', field('description', string))
  .assign('kind', field('kind', productCollectionKindDecoder))
  .assign('experiences', field('experiences', array(number)))
  .assign('display_per_page_count', field('display_per_page_count', number))
  .assign('browse_all', field('browse_all', boolean))
  .assign('display_button_label', field('display_button_label', explicitMaybe(string)))
  .assign('learning_design_state', field('learning_design_state', learningDesignStateDecoder))
  .assign('display_type', field('display_type', displayTypeDecoder));

const productCollectionResourceDecoder: Decoder<ProductCollectionResource> = field(
  'payload',
  productCollectionPayloadDecoder,
).map((payload): ProductCollectionResource => ({ payload, links: [] }));

const scheduledProgramDecoder: Decoder<ScheduledProgram> = succeed({})
  .assign('id', field('id', number))
  .assign('program_ids', field('program_ids', array(number)));

// Decodes only what learningCollectionEncoder reads from the template.
// Fields the clone discards (timestamps, licenses) default to empty rather
// than risking a decode failure on data we never send back.
const cloneTemplatePayloadDecoder: Decoder<LearningCollectionPayloadWithScheduledPrograms> =
  succeed({})
    .assign('id', field('id', explicitMaybe(number)))
    .assign('name', field('name', string))
    .assign('internal_name', field('internal_name', explicitMaybe(string)))
    .assign('description', field('description', string))
    .assign('search', field('search', boolean))
    .assign('client_identifier', field('client_identifier', string))
    .assign(
      'product_collections',
      field('product_collections', array(productCollectionResourceDecoder)),
    )
    .assign('scheduled_programs', field('scheduled_programs', array(scheduledProgramDecoder)))
    .assign('browse_all', field('browse_all', boolean))
    .assign(
      'has_directly_attached_experiences',
      field('has_directly_attached_experiences', boolean),
    )
    .assign('published_at', succeed(nothing<Date>()))
    .assign('updated_at', succeed(nothing<Date>()))
    .assign(
      'organization_licenses',
      succeed<LearningCollectionPayloadWithScheduledPrograms['organization_licenses']>([]),
    )
    .assign('licensed_type_ids', succeed<number[]>([]))
    .assign('has_promotables', succeed(false))
    .map((payload): LearningCollectionPayloadWithScheduledPrograms => payload);

export const cloneTemplateResourceDecoder: Decoder<LearningCollectionResourceWithScheduledPrograms> =
  field('payload', cloneTemplatePayloadDecoder).map(
    (payload): LearningCollectionResourceWithScheduledPrograms => ({ payload, links: [] }),
  );

// What the clone flow reads back from the create response: the new id, and
// the course ids the server kept (DVLP-7981: the server drops courses the
// organization can no longer offer; the difference from the source is what
// the admin is told about).
export interface ClonedCollection {
  id: Maybe<number>;
  experienceIds: number[];
}

const clonedCollectionDecoder: Decoder<ClonedCollection> = succeed({})
  .assign('id', field('id', explicitMaybe(number)))
  .assign(
    'experienceIds',
    field('product_collections', array(field('payload', field('experiences', array(number))))).map(
      (sections) => sections.flatMap((ids) => ids),
    ),
  );

export const clonedCollectionResourceDecoder: Decoder<Resource<ClonedCollection>> =
  resourceDecoder(clonedCollectionDecoder);
